import { i18n } from '@fs/zion-locale'
import { isValidPid as zionIsValidPid } from '@fs/zion-pid'
import accountSetupBG from '../images/backgrounds/bishop-landing-page.webp'
import saviorBaptismBG from '../images/backgrounds/Jesus-Baptism.webp'
import saviorBG from '../images/backgrounds/Jesus.webp'
import baptismalFontBG from '../images/backgrounds/baptismal-font.webp'
import identityBackground from '../images/backgrounds/identity.webp'
import { FAMILY_HISTORY } from './constants'

export const LCR_URL = 'https://lcr.churchofjesuschrist.org/'
export const MAX_ORD_READY_COUNT = 4
export const ORD_READY_TIMEOUT = 1.5 * 60 * 1000
export const TOKEN_TIMEOUT_TIME = 1200000

export const gedcomPrefix = 'http://gedcomx.org/Prefix'
export const gedcomGiven = 'http://gedcomx.org/Given'
export const gedcomSurname = 'http://gedcomx.org/Surname'
export const gedcomSuffix = 'http://gedcomx.org/Suffix'
export const gedcomBirth = 'http://gedcomx.org/Birth'
export const gedcomDeath = 'http://gedcomx.org/Death'
export const gedcomMarriage = 'http://gedcomx.org/Marriage'

export const publicAccountScreens = ['/account/review', '/account/connect', '/account/sign-out']

export function localizedMonthAbbreviation(date, locale = 'default', dateStyle = 'short') {
  const currentDate = new Date()
  currentDate.setDate(date)
  return currentDate.toLocaleDateString(locale, { day: 'numeric', month: dateStyle })
}

export function formatFormalDate(date) {
  let plusIndex = date?.standard?.formalText?.indexOf('+')
  if (Number.isNaN(plusIndex)) plusIndex = 0
  const plusPosition = plusIndex + 1
  const cleanDate = date?.standard?.formalText?.substr(plusPosition)?.replace('/', '')
  const dateArray = cleanDate?.split('-')
  if (date?.standard?.formalText) {
    if (dateArray.length === 1) {
      // Only a year was provided
      // Set it to the last day of the year in this case
      return new Date(cleanDate.concat('-12-31T00:00'))
    }
    if (dateArray.length === 2) {
      // No day was provided
      // Setting the day to 0 sets it to eh last day of the month
      dateArray.push(0)
      return new Date(...dateArray)
    }
    return new Date(cleanDate.concat('T00:00'))
  }
  return new Date(date?.primaryText)
}

export function formatForCode(code) {
  let formattedCode = ''
  if (code) {
    formattedCode = `${code[0]}-${/(?<=00).*/
      .exec(code)?.[0]
      .match(/.{4}|.{3}/g)
      .join('-')}`
  }
  return formattedCode
}

export function getPreferredNameForm(person) {
  return person?.nameForms?.find((form) => form?.lang === i18n.language) ?? person?.nameForms?.[0]
}

export function isLookupTokenExpired(startTime) {
  const callTime = getTimestamp()
  const timeToCall = callTime - startTime
  return timeToCall >= TOKEN_TIMEOUT_TIME
}

export function checkTreePersonNameWasEdited(currentTreePersonWasEdited, workingPerson, newNameForms) {
  let wasEdited = currentTreePersonWasEdited
  // Only check if the workingPerson is an existing tree person (indicated by having an `id`)
  if (workingPerson?.id) {
    const languages = newNameForms?.map((form) => form?.lang)
    languages?.forEach((lang) => {
      const oldNameForm = workingPerson?.nameForms?.find((form) => form?.lang === lang)
      const newNameForm = newNameForms?.find((form) => form?.lang === lang)

      const firstName = oldNameForm?.parts?.find((part) => part.type === gedcomGiven)?.value
      const lastName = oldNameForm?.parts?.find((part) => part.type === gedcomSurname)?.value
      const newFirstName = newNameForm?.parts?.find((part) => part.type === gedcomGiven)?.value
      const newLastName = newNameForm?.parts?.find((part) => part.type === gedcomSurname)?.value

      // NOTE: We shouldn't return `false` here in case a previous change was made. `treePersonWasEdited` should only ever be flipped to `true`
      if (firstName !== newFirstName || lastName !== newLastName) wasEdited = true
    })

    return wasEdited
  }

  return currentTreePersonWasEdited
}

// Only exported for testing purposes -- Used in otherhelper functions
export function checkTreePersonVitalsWereEdited(currentTreePersonWasEdited, workingPerson, newVitals) {
  // Only check if the workingPerson is an existing tree person (indicated by having an `id`)
  if (workingPerson?.id) {
    if (JSON.stringify(workingPerson?.birthDate) !== JSON.stringify(newVitals.birthDate)) return true
    if (JSON.stringify(workingPerson?.birthPlace) !== JSON.stringify(newVitals.birthPlace)) return true
    if (JSON.stringify(workingPerson?.marriageDate) !== JSON.stringify(newVitals.marriageDate)) return true
    if (JSON.stringify(workingPerson?.marriagePlace) !== JSON.stringify(newVitals.marriagePlace)) return true
    if (JSON.stringify(workingPerson?.deathDate) !== JSON.stringify(newVitals.deathDate)) return true
    if (JSON.stringify(workingPerson?.deathPlace) !== JSON.stringify(newVitals.deathPlace)) return true
  }

  return currentTreePersonWasEdited
}

export function determineGrandparentGap(connectingParent, isTryDemo) {
  if (!connectingParent) return { isMissing: true, isLiving: false }
  return {
    isMissing: !isValidPid(connectingParent.id, isTryDemo),
    isLiving: !!connectingParent?.living,
  }
}

const orderedSteps = [
  'relation',
  'tree-list',
  'name',
  'vitals',
  'confirm',
  'edit',
  'father',
  'mother',
  'spouse',
  'name-correction',
  'vitals-correction',
  'death-before-eight',
  'stillborn',
  'not-available',
  'ordinances-available-completed-reserved',
  'reserve',
  'names-ready',
  'print',
  'finish',
]
export function determineLatestStepCompleted(currentLatestCompletedStep, newCompletedStep) {
  // Return the latest step completed in the experience. If the user goes back a few steps, clicking "next" should not set this to a previous step.
  const currentIndex = orderedSteps.findIndex((step) => step === currentLatestCompletedStep)
  const newIndex = orderedSteps.findIndex((step) => step === newCompletedStep)

  return newIndex > currentIndex ? newCompletedStep : currentLatestCompletedStep
}

export function determineSex(sex, relationSpecific) {
  if (![undefined, 'UNKNOWN'].includes(sex)) return sex
  switch (relationSpecific) {
    case 'father':
    case 'grandfather':
    case 'grandparent1':
    case 'grandparent2':
    case 'husband':
    case 'son':
    case 'brother':
      return 'MALE'

    case 'mother':
    case 'grandmother':
    case 'grandparent21':
    case 'grandparent22':
    case 'wife':
    case 'daughter':
    case 'sister':
      return 'FEMALE'

    default:
      return 'UNKNOWN'
  }
}

export function getIsRtl() {
  return document.documentElement.getAttribute('dir') === 'rtl'
}

export function getBackgroundImage(page, step, templeBgImage) {
  // Family Name Cards Page
  if (page === 'assist' || (!page && !step)) return baptismalFontBG

  // Verify Account Page
  if (page === 'verify-account') return identityBackground

  // The QR Scan page should have a white background
  if (page === 'scan') return null

  // Any of the Bishop Landing Page steps
  if (page === 'account') return accountSetupBG

  // Any of the Add Name Flow steps
  switch (step) {
    case undefined: // Decision Tree Page has an undefined `step` value.
      return saviorBaptismBG

    case 'relation':
    case 'tree-list':
    case 'select-parents':
    case 'name':
    case 'vitals':
    case 'confirm':
    case 'edit':
    case 'father':
    case 'mother':
    case 'spouse':
    case 'match':
      return templeBgImage

    case 'ordinances-ready-reserve':
    case 'reserve':
    case 'name-correction':
    case 'vitals-correction':
    case 'death-before-eight':
    case 'stillborn':
    case 'not-available':
    case 'ordinances-available-completed-reserved':
      return saviorBG

    case 'names-ready':
    case 'print':
    case 'finish':
      return baptismalFontBG

    default:
      return null
  }
}

export function getGivenNames(person) {
  if (!person) return ''
  if (person?.nameForms) {
    const preferredNameForm = getPreferredNameForm(person)
    return preferredNameForm?.parts?.find((part) => part?.type === gedcomGiven)?.value
  }
  return ''
}

export function getSurnames(person) {
  if (!person) return ''
  if (person?.nameForms) {
    const preferredNameForm = getPreferredNameForm(person)
    return preferredNameForm?.parts?.find((part) => part?.type === gedcomSurname)?.value
  }
  return ''
}

export function getFullName(person) {
  if (!person) return ''
  const preferredNameForm = getPreferredNameForm(person)
  if (preferredNameForm) {
    const { fullText, firstName, lastName } = preferredNameForm
    if (fullText) return fullText

    let fullName = ''
    if (firstName) fullName = firstName
    if (firstName && lastName) fullName += ' '
    if (lastName) fullName += lastName
    return fullName
  }

  return ''
}

export function determineIfDateBeforeYear(dateToCheck, yearToCheck) {
  const year = new Date(yearToCheck)
  const cleanDate = dateToCheck?.standard?.formalText?.replace('+', '')
  const date = new Date(dateToCheck?.standard?.formalText ? `${cleanDate}T00:00` : dateToCheck?.primaryText)
  return date < year
}

export function determineIfDeathTooVague(dateToCheck) {
  const dateArray = dateToCheck?.standard?.formalText?.replace('+', '')?.split('-')
  const date = new Date()
  const currentYear = `${date.getFullYear()}`
  const vagueYears = [currentYear]

  let currentMonth = date.getMonth() + 1
  currentMonth = `${currentMonth < 10 ? '0' : ''}${currentMonth}`
  date.setDate(1)

  let previousMonth = date.getMonth()
  if (previousMonth === 0) {
    previousMonth = 12
    vagueYears.push(`${currentYear - 1}`)
  }
  previousMonth = `${previousMonth < 10 ? '0' : ''}${previousMonth}`
  const vagueMonths = [previousMonth, currentMonth]

  return (
    dateToCheck?.standard?.formalText &&
    ((dateArray.length === 1 && vagueYears.includes(dateArray[0])) ||
      (dateArray.length === 2 && vagueYears.includes(dateArray[0]) && vagueMonths.includes(dateArray[1])))
  )
}

export function determineIfDeceased30Days(dateToCheck) {
  if (!dateToCheck) return true
  const today = new Date()
  const deathDate = formatFormalDate(dateToCheck)
  // This is used to verify if the date is a valid date
  // eslint-disable-next-line no-restricted-globals
  const isValid = !isNaN(deathDate)
  const diffInDays =
    deathDate && isValid ? Math.floor((today.getTime() - deathDate.getTime()) / (1000 * 60 * 60 * 24)) : 31
  return isValid && diffInDays > 30
}

export function determineIfDeathBeforeBirth(workingPerson) {
  const birthDate = workingPerson?.birthDate ? formatFormalDate(workingPerson?.birthDate) : null
  const deathDate = workingPerson?.deathDate ? formatFormalDate(workingPerson?.deathDate) : null

  // This is used to verify if the date is a valid date
  // eslint-disable-next-line no-restricted-globals
  return birthDate && deathDate && !isNaN(birthDate) && !isNaN(deathDate) && birthDate > deathDate
}

export function determineIfYoungerThan8Years(deathDate, birthDate) {
  // Both dates must be present for this check, or else we can safely return `false`
  if (!deathDate || !birthDate) return false

  // Check both dates to be valid, if they're not both valid, then return `false`
  const formattedDeathDate = formatFormalDate(deathDate)
  const formattedBirthDate = formatFormalDate(birthDate)
  const isDeathValid = !Number.isNaN(formattedDeathDate)
  const isBirthValid = !Number.isNaN(formattedBirthDate)
  if (!isDeathValid || !isBirthValid) return false

  const eightYearBirthDay = new Date(
    `${formattedBirthDate.getFullYear() + 8}-${formattedBirthDate.getMonth() + 1}-${formattedBirthDate.getDate()}`
  )
  return eightYearBirthDay > formattedDeathDate
}

export function determineIfBornLessThan8YearsAgo(dateToCheck) {
  // The date must be present for this check, or else we can safely return `false`
  if (!dateToCheck) return false

  const today = new Date()
  const eightYearsAgo = new Date(`${today.getFullYear() - 8}-${today.getMonth() + 1}-${today.getDate()}`)
  const birthDate = dateToCheck ? formatFormalDate(dateToCheck) : null
  // This is used to verify if the date is a valid date
  // eslint-disable-next-line no-restricted-globals
  const isValid = !isNaN(birthDate)
  return isValid && birthDate > eightYearsAgo
}

export function determineIfBaptismAndConfirmationAreValue(baptism, confirmation, value) {
  return baptism?.status?.toUpperCase() === value && confirmation?.status?.toUpperCase() === value
}

export function determineIfBaptismOrConfirmationIsValue(baptism, confirmation, value) {
  return baptism?.status?.toUpperCase() === value || confirmation?.status?.toUpperCase() === value
}

export function determineIfOrdinanceIsValue(ordinance, value) {
  return ordinance?.status?.toUpperCase() === value
}

const nameReasons = [
  'INVALID_GIVEN_NAME_PIECE',
  'INVALID_NAME',
  'INVALID_SINGLE_NAME_PIECE',
  'INVALID_SPECIAL_CHARACTER_NAME',
  'INVALID_SURNAME',
  'INVALID_TITLE_GIVEN_MISSING',
  'MISTER_AS_ONLY_SURNAME',
  'NAME_CONTAINS_ONLY_INITIALS',
  'NAME_LANG_SCRIPT_MISMATCH',
  'NAME_LANG_UND_WITH_MULTIPLE_SCRIPTS',
  'NAME_TOO_LONG',
  'NO_NAME',
  'ONE_LATIN_LETTER_SURNAME',
  'REPEATING_SPECIAL_CHARACTER_NAME',
]
export function determineIfNameReason(statusReasons) {
  return statusReasons?.some((r) => nameReasons?.includes(r))
}

const vitalsReasons = ['MISSING_STANDARDIZED_DATE', 'MISSING_STANDARDIZED_PLACE', 'NOT_MATCHABLE_USING_EVENTS']
export function determineIfVitalsReason(statusReasons) {
  return statusReasons?.some((r) => vitalsReasons?.includes(r))
}

export function getTimestamp() {
  // Milliseconds since January 1st 1970 to act as timestamp
  return new Date().getTime()
}

export function updateTreePerson(dispatch, persons, checkName, checkVitals, lastStep, metadata, updatedData) {
  const workingPerson = persons[0]
  const { lastCompletedStep, treePersonWasEdited } = metadata
  const newMetadata = {
    ...metadata,
    lastCompletedStep: determineLatestStepCompleted(lastCompletedStep, lastStep),
  }

  const newPersons = structuredClone(persons)
  const workingPersonIndex = newPersons.findIndex((person) => person.id === workingPerson?.id)
  const newWorkingPerson = {
    ...workingPerson,
    ...updatedData,
    matchPurpose: 'TARGET',
  }
  newPersons[workingPersonIndex] = newWorkingPerson

  if (checkName) {
    newMetadata.treePersonWasEdited =
      newMetadata.treePersonWasEdited ||
      checkTreePersonNameWasEdited(treePersonWasEdited, workingPerson, updatedData?.nameForms)
  }
  if (checkVitals) {
    newMetadata.treePersonWasEdited =
      newMetadata.treePersonWasEdited ||
      checkTreePersonVitalsWereEdited(treePersonWasEdited, workingPerson, updatedData)
  }

  dispatch({
    type: 'SET_WORKING_PERSON_CONTAINER',
    metadata: newMetadata,
    persons: newPersons,
  })

  return { newMetadata, newPersons }
}

export function getRelationParameter(relation) {
  let relationParamter = ''
  switch (relation) {
    case 'child':
      relationParamter = 'CHILDREN'
      break
    case 'grandparent':
      relationParamter = 'GRANDPARENTS'
      break
    case 'parent':
      relationParamter = 'PARENTS'
      break
    case 'sibling':
      relationParamter = 'SIBLINGS'
      break
    case 'spouse':
      relationParamter = 'SPOUSES'
      break
    default:
  }
  return relationParamter
}

export function getGrandparentAscendancyTranslationCode(lineage, sex) {
  let lineageCode = lineage?.toLowerCase() === 'maternal' ? 'F' : 'M'
  if (!['maternal', 'paternal'].includes(lineage?.toLowerCase())) lineageCode = 'X'

  let sexCode = sex?.toLowerCase() === 'female' ? 'F' : 'M'
  if (!['male', 'female'].includes(sex?.toLowerCase())) sexCode = 'X'

  return lineageCode + sexCode
}

export function buildAscendancyCode(parentSex, grandparentSex) {
  let ascendancy = ''
  if (parentSex === 'MALE') ascendancy += 'M'
  else if (parentSex === 'FEMALE') ascendancy += 'F'
  else ascendancy += 'X'
  if (grandparentSex === 'MALE') ascendancy += 'M'
  else if (grandparentSex === 'FEMALE') ascendancy += 'F'
  else ascendancy += 'X'

  return ascendancy
}

export function getParent(persons, parentChildRel, parentRelationSpecific, focusedRelation, onAdd) {
  const selectedPerson = getWorkingPerson(persons)
  const isGrandparent = parentRelationSpecific.startsWith('grandparent')
  const isParent1 = parentRelationSpecific.endsWith('1')
  const targetParent = persons.find((parent) => parent.id === parentChildRel?.[isParent1 ? 'parent1' : 'parent2']?.id)
  const coParent = persons.find((parent) => parent.id === parentChildRel?.[isParent1 ? 'parent2' : 'parent1']?.id)
  let sex = ''

  if (!targetParent && coParent) {
    sex = coParent?.sex?.toUpperCase() === 'FEMALE' ? 'MALE' : 'FEMALE'
  } else if (!targetParent && !coParent) {
    sex = isParent1 ? 'MALE' : 'FEMALE'
  }
  const parentData = targetParent ?? { sex }

  let relationLabel = i18n.t('tree-list.label.parent', '{sex, select, female {Mother} male {Father} other {Parent}}', {
    sex: parentData.sex?.toLowerCase(),
  })
  if (isGrandparent) {
    const ascendancy = `X${parentData.sex.at(0)}`
    relationLabel = i18n.t(
      'tree-list.label.grandparent',
      '{ascendancy, select, XM {Grandfather} XF {Grandmother} MX {Paternal Grandparent} MM {Paternal Grandfather} MF {Paternal Grandmother} FX {Maternal Grandparent} FM {Maternal Grandfather} FF {Maternal Grandmother} other {Grandparent}}',
      { ascendancy }
    )
  }

  return {
    focusedRelation,
    isSelected: selectedPerson?.id && selectedPerson?.id === parentData.id,
    onAdd,
    person: parentData,
    relationLabel,
    relationSpecific: parentRelationSpecific,
  }
}

export function determineIfPersonReserved(pid, preparedPersons) {
  return preparedPersons?.some((person) => person?.id === pid)
}

const getLifeSpanDate = (formalDate) => {
  return formalDate ? new Date(`${formalDate?.slice(formalDate.lastIndexOf('+') + 1).replace(/\//g, '')}T00:00`) : null
}

export function getLifeSpan(person) {
  const beginDate = getLifeSpanDate(person?.lifespanBegin?.date?.formal)
  const endDate = getLifeSpanDate(person?.lifespanEnd?.date?.formal)
  if (!beginDate && !endDate) return ''
  return `${beginDate ? beginDate.getFullYear() : ''}-${endDate ? endDate.getFullYear() : ''}`
}

export function mergeMatchPerson(matchPerson, workingPerson) {
  workingPerson.id = matchPerson?.id
  const workingPersonNameForms = workingPerson?.nameForms?.map((form) => form?.lang)
  const newNameForms = [
    ...(workingPerson?.nameForms ?? []),
    ...(matchPerson?.nameForms ?? []).filter((form) => !workingPersonNameForms?.includes(form?.lang)),
  ]
  if (!workingPerson?.nameForms) workingPerson.nameForms = newNameForms
  if (!workingPerson?.birthDate) workingPerson.birthDate = matchPerson?.birthDate
  if (!workingPerson?.birthPlace) workingPerson.birthPlace = matchPerson?.birthPlace
  if (!workingPerson?.deathDate) workingPerson.deathDate = matchPerson?.deathDate
  if (!workingPerson?.deathPlace) workingPerson.deathPlace = matchPerson?.deathPlace
  return workingPerson
}

export function sortDelegates(delegates) {
  return delegates.sort((d1, d2) => {
    const d1IsConsultant = d1?.positionType === 'WARD_TEMPLE_AND_FAMILY_HISTORY_CONSULTANT'
    const d2IsConsultant = d2?.positionType === 'WARD_TEMPLE_AND_FAMILY_HISTORY_CONSULTANT'
    const d1IsLeader = d1?.positionType === 'BISHOP' || d1?.positionType?.includes('PRESIDENT')
    const d2IsLeader = d2?.positionType === 'BISHOP' || d2?.positionType?.includes('PRESIDENT')
    const d1IsFirstCounselor = d1?.positionType?.includes('FIRST_COUNSELOR')
    const d2IsFirstCounselor = d2?.positionType?.includes('FIRST_COUNSELOR')
    const d1IsSecondCounselor = d1?.positionType?.includes('SECOND_COUNSELOR')
    const d2IsSecondCounselor = d2?.positionType?.includes('SECOND_COUNSELOR')

    if (d1IsConsultant && d2IsConsultant) {
      return d1?.person?.displayNameLocal?.localeCompare(d2?.person?.displayNameLocal)
    }
    if (
      (d1IsConsultant && !d2IsConsultant) ||
      (!d1IsLeader && d2IsLeader) ||
      (d1IsSecondCounselor && d2IsFirstCounselor)
    )
      return 1
    if (
      (!d1IsConsultant && d2IsConsultant) ||
      (d1IsLeader && !d2IsLeader) ||
      (d1IsFirstCounselor && d2IsSecondCounselor)
    )
      return -1
    return 0
  })
}

export function getChildIds(match) {
  return match?.parentChildRelationshipsAsParent?.map((relationship) => relationship?.childId) ?? []
}

export function getParentIds(match) {
  return [
    ...new Set(match?.parentsPairings?.reduce((ids, pairing) => [...ids, pairing?.personId1, pairing?.personId2], [])),
  ]
}

export function getSiblingIds(match, parentIds) {
  return [
    ...new Set(
      parentIds
        ?.map((parentId) =>
          match?.relatives?.[parentId]?.parentChildRelationshipsAsParent?.map((relationship) => relationship?.childId)
        )
        .flat()
    ),
  ]
}

export function getSpouseIds(match) {
  return match?.couplesRelationships?.map((pairing) => pairing?.otherId) ?? []
}

function sortSpecializedHelpers(a, b) {
  if (a.positionType.includes('PRESIDENT') || a.positionType === 'BISHOP') return -1
  if (b.positionType.includes('PRESIDENT') || b.positionType === 'BISHOP') return 1
  if (a.positionType.includes('FIRST_COUNSELOR')) return -1
  if (b.positionType.includes('FIRST_COUNSELOR')) return 1
  if (a.positionType.includes('SECOND_COUNSELOR')) return -1
  return 1
}

function sortFhHelpers(a, b) {
  let aRank = 0
  let bRank = 0
  if (a.positionType.includes('_LEADER')) aRank = 1
  if (b.positionType.includes('_LEADER')) bRank = 1
  return bRank - aRank
}

export function getHelper(helpers, count = 1, isSpecialized = false) {
  if (!helpers) return null

  // Ignore FH consultants
  const sortedFilteredHelpers = helpers
    .filter((helper) =>
      isSpecialized ? !FAMILY_HISTORY.includes(helper.positionType) : FAMILY_HISTORY.includes(helper.positionType)
    )
    .sort(isSpecialized ? sortSpecializedHelpers : sortFhHelpers)
  if (!sortedFilteredHelpers.length) return null

  const helpersWithContactInfo = sortedFilteredHelpers.filter((helper) => helper.person.email || helper.person.phone)

  // If there are no specialized helpers with contact info, go with the leader
  if (!helpersWithContactInfo.length) return sortedFilteredHelpers.splice(0, count)

  // Find the highest-ranking leader with contact info to display
  return helpersWithContactInfo.splice(0, count)
}

// Get count of ordinance action between checked persons in preparedPersons list
export function getPersonsWithOrdinancesAction(preparedPersons, checkedStatuses, actionType) {
  return preparedPersons
    ?.filter((person) => checkedStatuses[person.id]) // Only include persons that are checked
    ?.filter((person) => {
      // If checking for `printable`, also filter out names that are shared (has `unShareable` set to `true`)
      return (
        actionType !== 'printable' ||
        (actionType === 'printable' && !person.reservations.some((reservation) => reservation.actions.unShareable))
      )
    })
    ?.filter((person) => person.reservations.some((reservation) => reservation.actions[actionType])) // Filter to just persons with ordinances with specified action type
}

export function removeFlagsButton() {
  // document.querySelector('button[aria-label="Open Flag Overrides"]').remove()
  document.querySelector('button[aria-label="Open Flag Overrides"]').style.visibility = 'hidden'
}

export function removeUsabillaButton() {
  // document.querySelector('button[data-testid="usabilla-btn"]').remove()
  document.querySelector('button[data-testid="usabilla-btn"]').parentNode.style.visibility = 'hidden'
}

export function createPCRelation(childId, parent1Id, parent2Id, isTryDemo) {
  return {
    child: !childId ? null : { id: childId, idType: isValidPid(childId, isTryDemo) ? 'TREE' : 'TEMPORARY' },
    parent1: !parent1Id ? null : { id: parent1Id, idType: isValidPid(parent1Id, isTryDemo) ? 'TREE' : 'TEMPORARY' },
    parent2: !parent2Id ? null : { id: parent2Id, idType: isValidPid(parent2Id, isTryDemo) ? 'TREE' : 'TEMPORARY' },
  }
}

export function getParentPairings(persons, parentChildRelationships, pid) {
  if (persons.constructor !== Array) throw new Error('getParentPairings(): persons must be an instance of Array.')
  if (parentChildRelationships.constructor !== Array)
    throw new Error('getParentPairings(): parentChildRelationships must be an instance of Array.')
  if (!persons.length || !parentChildRelationships.length || !pid) return []

  const parentsPairings = parentChildRelationships.filter((PCRelationship) => PCRelationship.child?.id === pid)
  return parentsPairings
}

export function createCoupleRelation(person1Id, person2Id, isTryDemo) {
  if (!person1Id || !person2Id) return null
  return {
    marriage: { type: gedcomMarriage },
    person1: {
      id: person1Id,
      idType: isValidPid(person1Id, isTryDemo) ? 'TREE' : 'TEMPORARY',
    },
    person2: {
      id: person2Id,
      idType: isValidPid(person2Id, isTryDemo) ? 'TREE' : 'TEMPORARY',
    },
  }
}

export function getCouplePairing(coupleRelationships, memberPid, spousePid) {
  if (coupleRelationships.constructor !== Array)
    throw new Error('getCouplePairing(): coupleRelationships must be an instance of Array.')
  if (!coupleRelationships.length || !spousePid || !memberPid) return {}

  return coupleRelationships.find(
    (relation) =>
      [memberPid, spousePid].includes(relation.person1.id) && [memberPid, spousePid].includes(relation.person2.id)
  )
}

export function getSpouses(persons, coupleRelationships, pid) {
  if (!Array.isArray(persons)) throw new Error('getSpouses(): persons must be an instance of Array.')
  if (!Array.isArray(coupleRelationships))
    throw new Error('getSpouses(): coupleRelationships must be an instance of Array.')
  if (!persons.length || !coupleRelationships.length || !pid) return []

  return coupleRelationships
    .filter((coupleRelationship) => {
      // Filter down to coupleRelationships that include the `pid`
      return [coupleRelationship.person1.id, coupleRelationship.person2.id].includes(pid)
    })
    .map((coupleRelationship) => {
      // Return the person object for the spouse
      const spouseId =
        coupleRelationship.person1.id === pid ? coupleRelationship.person2.id : coupleRelationship.person1.id
      return persons.find((person) => person?.id === spouseId)
    })
}

/**
 * Removes the 'TARGET' match purpose from the persons array.
 * @param {Array} persons - The array of persons.
 * @returns {Array} - The updated array of persons.
 * @throws {Error} - If persons is falsey or not an array.
 */
export function removeTargetFromPersons(persons) {
  if (!Array.isArray(persons)) throw new Error('The "persons" parameter must be an array.')
  if (!persons.length) return []

  // Perform a deep copy of the persons array
  const newPersons = structuredClone(persons)

  // In case multiple persons have 'matchPurpose', remove it from all of them
  const selectedPersons = newPersons.filter((person) => person.matchPurpose === 'TARGET')

  // Remove the 'matchPurpose' property from the person
  if (selectedPersons.length) selectedPersons.forEach((person) => delete person.matchPurpose)

  return newPersons
}

export function getSiblingsGroupedByParents(persons, parentChildRelationships, pid) {
  if (!Array.isArray(persons)) throw new Error('getSiblingsGroupedByParents(): persons must be an instance of Array.')
  if (!Array.isArray(parentChildRelationships))
    throw new Error('getSiblingsGroupedByParents(): parentChildRelationships must be an instance of Array.')
  if (!persons.length || !parentChildRelationships.length || !pid) return []

  // Find all Parent pairings containing the `pid` -- They will be unique parent pairings.
  const siblingsGroupedByParents = parentChildRelationships
    .filter((PCRelationship) => PCRelationship.child?.id === pid)
    .map((PCRelationship) => ({
      parent1: PCRelationship.parent1,
      parent2: PCRelationship.parent2,
      children: [PCRelationship.child],
    }))

  // Store children in the siblingsGroupedByParents array
  siblingsGroupedByParents.forEach((parentPairing) => {
    parentPairing.children = parentChildRelationships
      .filter(
        (PCRelationship) =>
          (pid !== PCRelationship.child?.id &&
            PCRelationship.parent1?.id === parentPairing.parent1?.id &&
            PCRelationship.parent2?.id === parentPairing.parent2?.id) ||
          (PCRelationship.parent2?.id === parentPairing.parent1?.id &&
            PCRelationship.parent1?.id === parentPairing.parent2?.id)
      )
      .map((PCRelationship) => persons.find((person) => person.id === PCRelationship.child.id))
  })

  return siblingsGroupedByParents
}

// Used in AccountSetupStepWrapper and AddNameFlowStepWrapper to determine if the navigational buttons are off-screen
function determineIfNavButtonsAreOffScreen() {
  const navButtonsElement = document.getElementById('nav-buttons-top')
  if (!navButtonsElement) return false

  // Add 40px to switch to fixed positioning when the buttons are half-way off-screen
  const navButtonsHeight = 40
  const navButtonsVerticalPadding = 8 * 2
  const { bottom } = navButtonsElement?.getBoundingClientRect() ?? {}
  return bottom + navButtonsHeight - navButtonsVerticalPadding > document.documentElement.clientHeight
}

// Used in AccountSetupStepWrapper and AddNameFlowStepWrapper to determine the height of the step card's content
function calculateContentHeight() {
  const cardContent = document.getElementById('content-height') ?? {
    scrollHeight: 0,
  }
  const cardPadding = 24 * 2 // top + bottom padding
  return cardContent.scrollHeight + cardPadding
}

export function setupPageHeightListeners(setPageHeight) {
  // By using a ref, this useEffect can update the value of `isMounted` before the functions run useState setters.
  // This will reduce the amount of console errors logged during tests.
  const isMountedRef = { isMounted: true }
  const updatePageHeight = () => {
    if (isMountedRef?.isMounted) setPageHeight(getPageHeight())
  }

  window.addEventListener('scroll', updatePageHeight)
  window.addEventListener('resize', updatePageHeight)

  return () => {
    isMountedRef.isMounted = false
    window.removeEventListener('scroll', updatePageHeight)
    window.removeEventListener('resize', updatePageHeight)
  }
}

export function setupStepWrapperListeners(setAreNavButtonsOffScreen, setContentHeight) {
  // By using a ref, this useEffect can update the value of `isMounted` before the functions run useState setters.
  // This will reduce the amount of console errors logged during tests.
  const isMountedRef = { isMounted: true }
  const scrollOffScreen = () => {
    const isOffScreen = determineIfNavButtonsAreOffScreen()
    if (isMountedRef?.isMounted) setAreNavButtonsOffScreen(isOffScreen)
  }
  const scrollContentHeight = () => {
    const cardContentHeight = calculateContentHeight()
    if (isMountedRef?.isMounted) setContentHeight(cardContentHeight)
  }
  const resizeOffScreen = () => {
    const isOffScreen = determineIfNavButtonsAreOffScreen()
    if (isMountedRef?.isMounted) setAreNavButtonsOffScreen(isOffScreen)
  }
  const resizeContentHeight = () => {
    const cardContentHeight = calculateContentHeight()
    if (isMountedRef?.isMounted) setContentHeight(cardContentHeight)
  }

  window.addEventListener('scroll', scrollOffScreen)
  window.addEventListener('scroll', scrollContentHeight)
  window.addEventListener('resize', resizeOffScreen)
  window.addEventListener('resize', resizeContentHeight)

  return () => {
    isMountedRef.isMounted = false
    window.removeEventListener('scroll', scrollOffScreen)
    window.removeEventListener('scroll', scrollContentHeight)
    window.removeEventListener('resize', resizeOffScreen)
    window.removeEventListener('resize', resizeContentHeight)
  }
}

export function runListenersOnInitialRender(setAreNavButtonsOffScreen, setContentHeight) {
  const isMountedRef = { isMounted: true }

  // Calling this within a setTimeout with 30ms set will force this update to be made after the DOM is setup. (Had issues with the /edit step and the nav buttons being clipped)
  setTimeout(() => {
    const cardContentHeight = calculateContentHeight()
    if (isMountedRef?.isMounted) setContentHeight(cardContentHeight)
  }, 60)
  setTimeout(() => {
    const isOffScreen = determineIfNavButtonsAreOffScreen()
    if (isMountedRef?.isMounted) setAreNavButtonsOffScreen(isOffScreen)
  }, 60)

  return () => {
    isMountedRef.isMounted = false
  }
}

export function getPageHeight() {
  const logoHeight = 62 // White Header with FS logo
  return window.innerHeight - logoHeight
}

export function filterPreparedNamesUserCanTakeToTemple(preparedPersons) {
  // This function filters out persons that the user has shared with the temple or with individuals.
  // It is used for determining whether to prevent the user from using Ordinances Ready.
  return preparedPersons?.filter(
    (person) => person.reservations[0]?.assignmentType === 'PERSONAL' && !person.sharedToIndividualInfo
  )
}

export function isFosterPerson(person) {
  const personParentTypes = person?.parentTypes
  // person is not foster if they have no parent types
  if (!personParentTypes || personParentTypes.length === 0) return false
  // person is not foster if they have at least one parent type that is not 'FOSTER_PARENT'
  return !personParentTypes.some((parentType) => parentType !== 'FOSTER_PARENT')
}

export function isValidPid(pid, isTryDemo) {
  const isValidTryDemoPattern = /^[A-Za-z0-9]{4}-[A-Za-z0-9]{3}$/
  return isTryDemo ? isValidTryDemoPattern.test(pid) : zionIsValidPid(pid)
}

export function isPlaceholderName(name, sex) {
  if (sex === 'MALE') return name === i18n.t('common.close-relation.father', 'Father')
  return name === i18n.t('common.close-relation.mother', 'Mother')
}

export function getPersonDisplayDate(date) {
  if (date?.standard?.standardizedText) {
    return date?.standard?.standardizedText
  }
  if (date?.normalized?.length > 0) {
    return date.normalized[0].value
  }
  if (date?.original) {
    return date.original
  }
  return date?.primaryText
}

export function getPersonDisplayPlace(place) {
  if (place?.standard?.standardizedText) {
    return place?.standard?.standardizedText
  }
  if (place?.names?.length > 0) {
    return place.names[0].value
  }
  if (place?.original) {
    return place.original
  }
  return place?.primaryText
}

export function replacePersonInRelationships(coupleRelationships, parentChildRelationships, oldPid, newPid) {
  // NOTE: This replaces a pid in the relationships array with a new pid. This is used when the user has selected a match person.
  //       It is also used in the FtteRedirectionLayer to revert the pid back to the original pid.

  // Update coupleRelationships, replacing workingPerson's id with match's id
  const newCoupleRelationships = structuredClone(coupleRelationships)
  newCoupleRelationships.forEach((coupleRelationship) => {
    if (coupleRelationship.person1.id === oldPid) coupleRelationship.person1.id = newPid
    else if (coupleRelationship.person2.id === oldPid) coupleRelationship.person2.id = newPid
  })

  // Update ParentChildRelationships, replacing workingPerson's id with match's id
  const newParentChildRelationships = structuredClone(parentChildRelationships)
  newParentChildRelationships.forEach((parentChildRelationship) => {
    if (parentChildRelationship.parent1?.id === oldPid) parentChildRelationship.parent1.id = newPid
    else if (parentChildRelationship.parent2?.id === oldPid) parentChildRelationship.parent2.id = newPid
    else if (parentChildRelationship.child?.id === oldPid) parentChildRelationship.child.id = newPid
  })

  return { newCoupleRelationships, newParentChildRelationships }
}

export function getWorkingPerson(persons) {
  if (!persons || persons.length === 0) return null
  return persons.find((person) => person.matchPurpose === 'TARGET')
}
